import { Palette } from '@mui/material';
import {
  OxCategoriesKeys,
  OxCategoriesNames,
} from '@oxappsec/ox-consolidated-categories';
import { IconType, OneOfValues } from 'common-types';
import { ChangeEvent } from 'react';
import { Nullable } from 'common-types';
import { RepoNode } from '../onboarding/onboarding-types';
import { CredentialsType } from './connector-configure-types';

export enum EConnectorFields {
  id = 'id',
  name = 'name',
  displayName = 'displayName',
  description = 'description',
  hostURL = 'hostURL',
  iconURL = 'iconURL',
  family = 'family',
  credentialsType = 'credentialsType',
  credentialsTypes = 'credentialsTypes',
  credentials = 'credentials',
  isConfigured = 'isConfigured',
  errorMessage = 'errorMessage',
  isResourceAvailable = 'isResourceAvailable',
  isOxBuiltIn = 'isOxBuiltIn',
  isOpenSource = 'isOpenSource',
  openSourceWebsiteUrl = 'openSourceWebsiteUrl',
  openSourceLicense = 'openSourceLicense',
  openSourceAuthor = 'openSourceAuthor',
  defaultEnabled = 'defaultEnabled',
  comingSoon = 'comingSoon',
  aliasFor = 'aliasFor',
  identityProviderInfo = 'identityProviderInfo',
  awsCloudFormationInfo = 'awsCloudFormationInfo',
  awsCloudFormationOrganizationInfo = 'awsCloudFormationOrganizationInfo',
  connectionInstructions = 'connectionInstructions',
  isConnectorInvalid = 'isConnectorInvalid',
  isDiscovered = 'isDiscovered',
  isEmptyOfRepos = 'isEmptyOfRepos',
  connectorToUse = 'connectorToUse',
  connectorStatus = 'connectorStatus',
  badgeIcon = 'badgeIcon',
}

export interface Connector {
  [EConnectorFields.id]: string;
  [EConnectorFields.name]: string;
  [EConnectorFields.displayName]: string;
  [EConnectorFields.description]: string;
  [EConnectorFields.hostURL]: string;
  [EConnectorFields.iconURL]: string;
  [EConnectorFields.family]: OneOfValues<typeof OxCategoriesKeys>;
  [EConnectorFields.credentialsType]: CredentialsType;
  [EConnectorFields.credentialsTypes]: CredentialsType[];
  [EConnectorFields.credentials]: Credential[];
  [EConnectorFields.isConfigured]: boolean;
  [EConnectorFields.errorMessage]?: string;
  [EConnectorFields.isResourceAvailable]: boolean;
  [EConnectorFields.isOxBuiltIn]: boolean;
  [EConnectorFields.isOpenSource]: boolean;
  [EConnectorFields.openSourceWebsiteUrl]?: string;
  [EConnectorFields.openSourceLicense]?: string;
  [EConnectorFields.openSourceAuthor]?: string;
  [EConnectorFields.defaultEnabled]?: boolean;
  [EConnectorFields.comingSoon]?: boolean;
  [EConnectorFields.aliasFor]?: string;
  [EConnectorFields.identityProviderInfo]?: IdentityProviderInfo;
  [EConnectorFields.awsCloudFormationInfo]?: AWSCloudFormationInfo;
  [EConnectorFields.awsCloudFormationOrganizationInfo]?: AWSCloudFormationInfo;
  [EConnectorFields.connectionInstructions]?: ConnectorInstructionsType[];
  [EConnectorFields.isConnectorInvalid]?: boolean;
  [EConnectorFields.isDiscovered]?: boolean;
  [EConnectorFields.isEmptyOfRepos]?: boolean;
  [EConnectorFields.connectorToUse]?: Connector;
  [EConnectorFields.connectorStatus]: ConnectorStatus;
  [EConnectorFields.badgeIcon]?: IconType;
}

export enum CollectionType {
  Collectors = 'collectors',
}

export interface ConnectorStatus {
  title: ConnectorStatusTitle;
  color: keyof Palette['badge'];
}

export enum ConnectorStatusTitle {
  NotConnected = 'Discovered - Not Connected',
  ConnectivityIssue = 'Connectivity Issue',
  Connected = 'Connected',
}

export interface ConnectorConfigureResponse {
  id: string;
  isConfigured: boolean;
}

export interface CredentialsInput {
  name: string;
  password: string;
  token: string;
  clientId: string;
  secretKey: string;
  awsRoleArn: string;
  awsExternalId: string;
  awsAccessKey: string;
  awsAccessSecret: string;
  tenant: string;
}

export interface CredentialFormFields {
  name: string;
  password: string;
  token: string;
  hostURL: string;
  clientId: string;
  secretKey: string;
  awsRoleArn: string;
  awsExternalId: string;
  awsAccessKey: string;
  awsAccessSecret: string;
  tenant: string;
}

export interface GetConnectorInput {
  connectorID: string;
}

export interface AddCredentialsInput {
  connectorID: string;
  hostURL: string;
  credentialsType: CredentialsType;
  credentialsInput: CredentialsInput;
}

export interface RemoveCredentialsInput {
  connectorID: string;
  credentialsIndex: number;
}

export interface CredentialsConfigureResponse {
  id: string;
  hostURL: string;
  isConfigured: boolean;
  credentials: Credential[];
}

export interface ValidateSingleConnectorResponse {
  verifySingleConnectorCredentials: {
    credentialsAreValid: boolean;
    noReposFound: boolean;
  };
}

export interface ValidateAllCredentialsResponse {
  verifyConnectorsCredentials: VerifyConnectorsCredentialsObj;
}

export interface VerifyConnectorsCredentialsObj {
  verificationComplete: boolean;
  invalidCredentialsConnectors: string[];
}

export interface ValidateSingleConnectorInput {
  connectorID: string;
}

export interface EnableConnectorInput {
  connectorID: string;
}

export interface DisableConnectorInput {
  connectorID: string;
}

export interface ConnectorEnableDisableResponse {
  isConfigured: boolean;
  id: string;
}

export interface CredentialsBase {
  tokenExpirationDate?: string;
  credentialsType?: string;
}

export interface TokenCredentials extends CredentialsBase {
  token: string;
}

export interface UserPasswordCredentials extends CredentialsBase {
  name: string;
  password: string;
}

export interface UserPasswordAndTenantCredentials extends CredentialsBase {
  tenant: string;
  name: string;
  password: string;
}

export interface TokenAndUserCredentials extends CredentialsBase {
  name: string;
  token: string;
}

export interface ClientIdSecretKeyCredentials extends CredentialsBase {
  clientId: string;
  secretKey: string;
}

export interface AWSAssumeRoleCredentials extends CredentialsBase {
  awsRoleArn?: string;
  awsExternalId?: string;
}
export interface AWSAssumeRoleCredentialsOnprem
  extends AWSAssumeRoleCredentials {
  awsAccessKey?: string;
  awsAccessSecret?: string;
}
export interface CredentialError {
  message: string;
}

export type Credential =
  | UserPasswordAndTenantCredentials
  | TokenCredentials
  | UserPasswordCredentials
  | TokenAndUserCredentials
  | ClientIdSecretKeyCredentials;

export type ConnectorFieldValue =
  | string
  | boolean
  | CredentialsType
  | Credential[]
  | undefined;

export enum ConnectorErrorMessages {
  EnableFailed = 'Enabling connector failed due to an error',
  DisableFailed = 'Disabling connector failed due to an error',
}

export interface ConnectorResponseObject {
  connector: Connector;
}

export interface SingleConnectorResponseObject {
  getConnector: Connector;
}

export interface GetConnectorResourcesInput {
  connectorID: string;
}

export interface GetConnectorResourcesResponse {
  getConnectorResources: {
    resources: RepoNode[];
  };
}

export interface MonitorConnectorResourcesInput {
  connectorID: string;
  resources: Nullable<Omit<RepoNode, 'children'>[]>;
  monitorAllResources?: boolean;
  monitorAllNewlyCreatedResources?: number;
}

export interface MonitorConnectorResourcesResponse {
  monitorConnectorResources: {
    success: boolean;
  };
}

export interface ConnectorsByFamilyObject {
  family: Connector['family'];
  familyDisplayName: OneOfValues<typeof OxCategoriesNames>;
  connectors: Connector[];
}

export interface onboardingConnectorsObject {
  connectors: Connector[];
}

export interface onboardingConnectorsObjectResponse {
  connectors: ConnectorResponseObject[];
}

export interface ConnectorsByFamilyObjectResponse
  extends Omit<ConnectorsByFamilyObject, 'connectors'> {
  connectors: ConnectorResponseObject[];
}

export interface IdentityProviderInfo {
  baseURL: string;
  urlParams: string;
  scope: string;
  configText: string;
}

export interface AWSCloudFormationInfo {
  baseURL: string;
  urlParams: string;
}

export interface ConnectorInstructionsType {
  type: string;
  title?: string;
  details?: string[];
  linksToDocs?: {
    href: string;
    title: string;
  }[];
}
export interface IdentityProviderInput {
  code: string;
  state: string;
  identityProviderBaseURL: string;
}

export interface IConnectorCredentialsFormProps {
  values: CredentialFormFields;
  handleOnChange: (event: ChangeEvent<HTMLInputElement>) => void;
  isConnectorConfigured: boolean;
  areCredentialsExpired: boolean;
  credentialsExpirationMsg: string;
  errorMessage?: string;
  credentialsType?: CredentialsType;
  connector: Connector;
  tenantFieldRequired: boolean;
  onpremAWSCredentialsRequired: boolean;
  tokenLabelField: string;
  isReadOnlyRole: boolean;
}

export interface IConnectorHostURLProps {
  hasError: boolean;
  name: string;
  value: string;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  isDisabled: boolean;
}

export interface ICredentialsFormSwitchProps {
  credentialsFormProps: IConnectorCredentialsFormProps;
  credentialsType: CredentialsType;
}

export interface IConnectorActionsGroupProps {
  onRemoveCredentials?: React.MouseEventHandler<HTMLButtonElement>;
  onVerifyAndAdd?: React.MouseEventHandler<HTMLButtonElement>;
  validateConnector?: () => void;
  openConnectorSettings: () => void;
  connectorValidationInProgress: boolean;
}
export interface IConnectorButtonGroupProps
  extends IConnectorActionsGroupProps {
  disabledConnect: boolean;
  isConnectorConfigured: boolean;
  showValidationResult: boolean;
  connectorIsValid: boolean;
  isScanning: boolean;
}

export enum ConnectorsIDPErrors {
  MissingCode = 'missingIdpCode',
  MissingState = 'missingIdpState',
  AccessDenied = 'idpAccessDenied',
  ValidationError = 'validationError',
  GeneralError = 'generalIdpError',
}
